import { zodResolver } from '@hookform/resolvers/zod';
import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { AuthRouteLayout } from '@/components/auth/auth-route-layout';
import { LoginEntryLayout } from '@/components/auth/login-entry-layout';
import { DesktopEmailCodePanel } from '@/components/auth/desktop-email-code-panel';
import { MFALoginView } from '@/components/auth/mfa/MFALoginView';
import { PasskeySignInButton } from '@/components/auth/passkey-sign-in-button';
import SocialSignInButtons from '@/components/auth/social-sign-in-buttons';
import { logAuthError } from '@/components/auth/log-auth-error';
import { createFormSchema } from '@/components/auth/login-form-schema';
import { useEmailCodeSignIn } from '@/components/auth/use-email-code-sign-in';
import { usePasskeySignIn } from '@/components/auth/use-passkey-sign-in';
import { usePasswordSignIn } from '@/components/auth/use-password-sign-in';
import { MobileBootScreen } from '@/components/mobile/MobileBootScreen';
import { usePrefetchAppShell } from '@/hooks/use-prefetch-app-shell';
import { useToast } from '@/hooks/use-toast';
import { completeLogin as completeLoginUtil } from '@/lib/auth-utils';
import { isDesktopPlatform } from '@/lib/desktop/bridge';
import { configureDesktopAuthPersistence } from '@/lib/mobile/secure-storage-adapter';
import { isNativePlatform } from '@/lib/mobile/platform';

import { LoginPasswordForm } from './login-password-form';

import type { LoginFormData } from '@/components/auth/login-form-schema';
import type { MfaStepUpController } from '@/components/auth/use-passkey-sign-in';
import type { ReactNode } from 'react';

const MobileLoginScreen = lazy(async () => await import('./mobile/login-mobile'));
/** Which sign-in flow owns the MFA step-up currently on screen. */
type MfaSource = 'password' | 'passkey' | 'emailCode';

/**
 * Desktop sessions are unconditionally persistent, so there is no preference to
 * carry between mounts - the previous sessionStorage round-trip existed only to
 * remember an opt-out that no longer exists.
 */
function enableDesktopPersistentSession(): void {
  configureDesktopAuthPersistence(true);
}

const WebLogin = () => {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const { toast } = useToast();
  const desktop = isDesktopPlatform();
  const [isSocialSignInLoading, setIsSocialSignInLoading] = useState(false);
  const [mfaSource, setMfaSource] = useState<MfaSource>('password');
  // Desktop only. The web column keeps the secure-link route in that slot, so
  // this never opens there and the panel is never mounted off the entry canvas.
  const [emailCodeOpen, setEmailCodeOpen] = useState(false);
  const formSchema = useMemo(
    () =>
      createFormSchema({
        invalidEmail: t('login.invalidEmail'),
        passwordRequired: t('login.passwordRequired'),
      }),
    [t],
  );

  const form = useForm<LoginFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      password: '',
      // Always true in the desktop app, with no control to turn it off. An
      // installed app that signs you out when you quit it is broken, not
      // careful - the token already lives in the OS keychain under the user's
      // own account, which is the boundary that matters. The web app keeps the
      // checkbox because a browser is routinely a shared or borrowed one.
      rememberMe: desktop,
    },
  });

  const rememberMe = useWatch({ control: form.control, name: 'rememberMe' });

  const handleSocialLoadingChange = useCallback(
    (loading: boolean) => {
      if (desktop && loading) {
        enableDesktopPersistentSession();
      }
      setIsSocialSignInLoading(loading);
    },
    [desktop],
  );

  // Re-asserted on every desktop mount rather than left to the form default:
  // a session-only choice made before this became unconditional is stored in
  // localStorage, so without this an existing install keeps signing itself out.
  useEffect(() => {
    if (desktop) {
      enableDesktopPersistentSession();
    }
  }, [desktop]);

  const handleSignedIn = async (userId: string) => {
    const result = await completeLoginUtil(userId);
    if (result.error) {
      toast({
        title: t('login.unableTitle'),
        description: result.error,
        variant: 'destructive',
      });
      return;
    }

    if (result.shouldProceed) {
      try {
        await navigate(result.redirectPath, { replace: true });
      } catch {
        console.error('Login navigation failed');
        globalThis.location.href = result.redirectPath;
      }
    }
  };

  const passwordSignIn = usePasswordSignIn({
    onSignedIn: handleSignedIn,
    logError: logAuthError,
  });
  const passkeySignIn = usePasskeySignIn({ onSignedIn: handleSignedIn, logError: logAuthError });
  const emailCodeSignIn = useEmailCodeSignIn({
    onSignedIn: handleSignedIn,
    logError: logAuthError,
  });
  const loginControlsDisabled =
    passwordSignIn.isLoading ||
    passkeySignIn.isLoading ||
    emailCodeSignIn.isLoading ||
    isSocialSignInLoading;

  // ONE step-up controller: the source discriminant picks the whole bundle, so
  // the view on screen and the `verifyMFA` that receives the code always come
  // from the same hook. `showA || showB` with shared wiring hands the code to
  // whichever hook holds no factor id, where it silently no-ops.
  const stepUpControllers: Record<MfaSource, MfaStepUpController> = {
    password: passwordSignIn,
    passkey: passkeySignIn,
    emailCode: emailCodeSignIn,
  };
  const stepUp: MfaStepUpController = stepUpControllers[mfaSource];

  // Every operation that can surface a step-up claims the controller through
  // here, retries included: a handler that runs the check without claiming
  // mounts nothing, because `stepUp` still resolves to the other hook. Claiming
  // and releasing are one decision, so they live in one function — an effect
  // keyed on `mfaSource` would need `useEffectEvent` to survive the hook's
  // per-render identity and would still fire spuriously on mount.
  const claimStepUp = (source: MfaSource): void => {
    setMfaSource(source);
    // Any source but the passkey one is a takeover from its point of view, so
    // the test is "not passkey" rather than a list of the flows that exist.
    if (source !== 'passkey') {
      passkeySignIn.releaseStepUp();
    }
  };

  // Warm the post-login route chunks while the form sits idle. Pausing during
  // any sign-in path (password, social, passkey ceremony) avoids contending with
  // the in-flight auth request.
  usePrefetchAppShell(!loginControlsDisabled);

  let content: ReactNode;
  if (stepUp.showMFAInput) {
    content = (
      <MFALoginView
        isVerifying={stepUp.isLoading}
        userEmail={stepUp.userEmail}
        // Every implementation signs out internally and two return a promise for
        // it, so the call is wrapped and its result discarded rather than handed
        // straight to a void-typed prop - nothing here should ever start
        // awaiting it by accident.
        //
        // The panel closes with it, so this control lands where its label says:
        // the sign-in screen. Unconditional because the panel can only be open
        // when the step-up on screen came from it - the password form is not
        // reachable while it is up.
        onBack={() => {
          stepUp.dismissMFA();
          setEmailCodeOpen(false);
        }}
        onVerify={stepUp.verifyMFA}
      />
    );
  } else if (emailCodeOpen) {
    content = (
      <DesktopEmailCodePanel
        isLoading={emailCodeSignIn.isLoading}
        notice={emailCodeSignIn.notice}
        pendingEmail={emailCodeSignIn.pendingEmail}
        step={emailCodeSignIn.step}
        onClearNotice={emailCodeSignIn.clearNotice}
        // `restart` before the panel closes, not after: it is the call that
        // revokes a session still owing a step-up, and leaving the panel is
        // exactly the exit that would otherwise strand one.
        onClose={() => {
          emailCodeSignIn.restart();
          setEmailCodeOpen(false);
        }}
        onRetryStepUp={() => {
          claimStepUp('emailCode');
          // The hook resolves its own check failures back into `notice`, so a
          // rejection here would be a bug in it - surfaced, never swallowed.
          emailCodeSignIn.retryStepUpCheck().catch((error: unknown) => {
            logAuthError('emailCodeRetryRejected', error);
          });
        }}
        onSendCode={(address) => {
          emailCodeSignIn.sendCode(address).catch((error: unknown) => {
            logAuthError('emailCodeSendRejected', error);
          });
        }}
        onUseDifferentEmail={emailCodeSignIn.restart}
        onVerifyCode={(code) => {
          enableDesktopPersistentSession();
          claimStepUp('emailCode');
          emailCodeSignIn.verifyCode(code).catch((error: unknown) => {
            logAuthError('emailCodeVerifyRejected', error);
          });
        }}
      />
    );
  } else {
    content = (
      <LoginEntryLayout
        desktop={desktop}
        form={form}
        isDisabled={loginControlsDisabled}
        methods={
          <>
            <SocialSignInButtons
              isDisabled={loginControlsDisabled}
              variant={desktop ? 'stack' : 'tiles'}
              onEmailCodeClick={() => setEmailCodeOpen(true)}
              onLoadingChange={handleSocialLoadingChange}
              onMagicLinkClick={() => {
                Promise.resolve(navigate('/auth/magic-link')).catch(() => null);
              }}
            />

            {desktop ? (
              <p className="mt-3 text-center text-xs text-entry-ink/45" role="note">
                {t('desktopLogin.browserHandoff')}
              </p>
            ) : (
              <PasskeySignInButton
                isDisabled={loginControlsDisabled}
                isPending={passkeySignIn.isLoading}
                notice={passkeySignIn.notice}
                variant="tile"
                onRetry={() => {
                  claimStepUp('passkey');
                  // Both hook entry points resolve their own failures into `notice`, so a
                  // rejection here would be a bug in the hook - surfaced, never swallowed.
                  passkeySignIn.retryStepUpCheck().catch((error: unknown) => {
                    logAuthError('passkeyRetryRejected', error);
                  });
                }}
                onSignIn={() => {
                  claimStepUp('passkey');
                  passkeySignIn.startPasskeySignIn().catch((error: unknown) => {
                    logAuthError('passkeyStartRejected', error);
                  });
                }}
              />
            )}
          </>
        }
      >
        <LoginPasswordForm
          desktop={desktop}
          form={form}
          isDisabled={loginControlsDisabled}
          isLoading={passwordSignIn.isLoading}
          notice={passwordSignIn.notice}
          rememberMe={rememberMe}
          onResetPassword={() => {
            Promise.resolve(navigate('/auth/reset-password')).catch(() => null);
          }}
          onRetry={() => {
            claimStepUp('password');
            // The hook resolves its own failures back into `notice`, so a
            // rejection here would be a bug in it - surfaced, never swallowed.
            passwordSignIn.retryStepUpCheck().catch((error: unknown) => {
              logAuthError('passwordRetryRejected', error);
            });
          }}
          onSubmit={async (values) => {
            if (desktop) {
              enableDesktopPersistentSession();
            }
            claimStepUp('password');
            await passwordSignIn.signInWithPassword(values);
          }}
        />
      </LoginEntryLayout>
    );
  }

  return <AuthRouteLayout>{content}</AuthRouteLayout>;
};

const Login = () =>
  isNativePlatform() ? (
    <Suspense fallback={<MobileBootScreen />}>
      <MobileLoginScreen />
    </Suspense>
  ) : (
    <WebLogin />
  );

export default Login;
